<script setup lang="ts">
  /**
   * Order summary — 03-components/14-order-summary · OSCloud DS 1.1.170
   */
  import { useId } from 'vue'
  import BfIconLock from '@/components/ui/icons/BfIconLock.vue'
  import BfIconShield from '@/components/ui/icons/BfIconShield.vue'
  import type {
    OrderSummaryItem,
    OrderSummarySavings,
    OrderSummaryTotal,
  } from '@/components/ui/bahnhof/BfOrderSummaryTypes'

  withDefaults(
    defineProps<{
      /** Uppercase eyebrow above the title. */
      eyebrow?: string
      /** Panel heading (e.g. "3 invoices selected"). */
      title: string
      /** Itemised list rows. */
      items?: OrderSummaryItem[]
      /** Totals rows (sub total, VAT, …) shown above the grand total. */
      totals?: OrderSummaryTotal[]
      /** The savings row in the foot, directly under the grand total. */
      footSavings?: OrderSummarySavings | null
      /** Grand-total label. */
      totalLabel?: string
      /** Grand-total amount, already formatted (e.g. "38.25"). */
      totalValue: string
      /** Optional muted currency suffix rendered after the amount (e.g. "EUR"). */
      totalCurrency?: string
      /** Optional reassurance line with a lock icon in the footer. */
      secureText?: string
      /** The reassurance line's icon: the lock where a card is charged, the shield where nothing is paid yet. */
      secureIcon?: 'lock' | 'shield'
      /** Stick to the top on wide viewports; falls back to static ≤980px. */
      sticky?: boolean
    }>(),
    {
      eyebrow: 'Order summary',
      items: () => [],
      totals: () => [],
      footSavings: null,
      totalLabel: 'Total due',
      totalCurrency: '',
      secureText: '',
      secureIcon: 'lock',
      sticky: true,
    },
  )

  const titleId = useId()
</script>

<template>
  <aside class="order-summary" :class="{ 'order-summary--sticky': sticky }" :aria-labelledby="titleId">
    <div class="order-summary__head">
      <div v-if="eyebrow" class="order-summary__eyebrow">{{ eyebrow }}</div>
      <!-- The slot lets the title carry a link back to what is being paid for. -->
      <div :id="titleId" class="order-summary__title">
        <slot name="title">{{ title }}</slot>
      </div>
    </div>

    <div v-if="items.length" class="order-summary__items">
      <div
        v-for="item in items"
        :key="item.id"
        class="order-summary__row"
        :class="{ 'order-summary__row--secondary': item.muted, 'order-summary__row--savings': item.savings }"
      >
        <div class="order-summary__label">
          <div class="order-summary__label-main">{{ item.main }}</div>
          <div v-if="item.sub" class="order-summary__label-sub">{{ item.sub }}</div>
        </div>
        <div class="order-summary__value">{{ item.value }}</div>
      </div>
    </div>

    <div v-if="totals.length" class="order-summary__totals">
      <div
        v-for="(total, index) in totals"
        :key="index"
        class="order-summary__row"
        :class="{ 'order-summary__row--secondary': total.muted, 'order-summary__row--savings': total.savings }"
      >
        <div class="order-summary__label">
          <div class="order-summary__label-main">{{ total.label }}</div>
        </div>
        <div class="order-summary__value">{{ total.value }}</div>
      </div>
    </div>

    <div class="order-summary__foot">
      <div class="order-summary__totals-row">
        <span class="order-summary__total-label">{{ totalLabel }}</span>
        <span class="order-summary__total-value">
          {{ totalValue }}
          <span v-if="totalCurrency" class="order-summary__total-cur">{{ totalCurrency }}</span>
        </span>
      </div>

      <div
        v-if="footSavings"
        class="order-summary__row order-summary__row--savings"
        :class="{ 'order-summary__row--secondary': footSavings.muted }"
      >
        <div class="order-summary__label">
          <div class="order-summary__label-main">{{ footSavings.label }}</div>
        </div>
        <div class="order-summary__value">{{ footSavings.value }}</div>
      </div>

      <!-- Consumer supplies the CTA (label, action, disabled state) and any help text. -->
      <slot name="action" />

      <div v-if="secureText" class="order-summary__security-note">
        <BfIconShield v-if="secureIcon === 'shield'" class="order-summary__security-note-icon" :size="16" />
        <BfIconLock v-else class="order-summary__security-note-icon" :size="16" />
        {{ secureText }}
      </div>
    </div>
  </aside>
</template>

<style scoped lang="scss">
  .order-summary {
    background: var(--bf-white);
    border: 1px solid var(--bf-line);
    border-radius: var(--bf-r-lg);
    box-shadow: var(--bf-shadow-2);
    overflow: hidden;
  }

  .order-summary--sticky {
    position: sticky;
    top: 8px;
  }

  .order-summary__head {
    padding: 18px 22px;
    border-bottom: 1px solid var(--bf-line);
  }

  .order-summary__eyebrow {
    margin-bottom: 6px;
    font-family: var(--bf-font-display);
    font-size: 11px;
    font-weight: var(--bf-fw-bold);
    color: var(--bf-orange-dark);
    text-transform: uppercase;
    letter-spacing: var(--bf-tr-eyebrow);
  }

  .order-summary__title {
    font-family: var(--bf-font-display);
    font-size: var(--bf-fs-md);
    font-weight: var(--bf-fw-semibold);
    color: var(--bf-deep-blue);
  }

  .order-summary__items {
    padding: 8px 22px;
  }

  .order-summary__totals {
    padding: 8px 22px;
    border-top: 1px solid var(--bf-line);
  }

  .order-summary__row {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 10px;
    padding: 9px 0;
  }

  .order-summary__label {
    min-width: 0;
  }

  .order-summary__label-main {
    font-family: var(--bf-font-display);
    font-size: var(--bf-fs-sm);
    font-weight: var(--bf-fw-semibold);
    color: var(--bf-dark-blue);
  }

  .order-summary__label-sub {
    margin-top: 2px;
    font-family: var(--bf-font-body);
    font-size: 12.5px;
    color: var(--bf-dusky-2);
  }

  .order-summary__value {
    font-family: var(--bf-font-display);
    font-size: var(--bf-fs-sm);
    font-weight: var(--bf-fw-semibold);
    color: var(--bf-deep-blue);
    font-variant-numeric: tabular-nums;
    white-space: nowrap;
  }

  .order-summary__row--secondary .order-summary__label-main {
    font-family: var(--bf-font-body);
    font-weight: var(--bf-fw-regular);
    color: var(--bf-dusky-2);
  }

  .order-summary__row--secondary .order-summary__value {
    font-weight: var(--bf-fw-medium);
    color: var(--bf-dusky-2);
  }

  .order-summary__row--savings .order-summary__label-main {
    font-family: var(--bf-font-body);
    font-weight: var(--bf-fw-semibold);
    color: var(--bf-green-deep);
  }

  .order-summary__row--savings .order-summary__value {
    color: var(--bf-green-deep);
  }

  /* Grey savings (DS 1.1.170): composed with __row--secondary, the secondary tone wins. */
  .order-summary__row--savings.order-summary__row--secondary .order-summary__label-main {
    font-weight: var(--bf-fw-regular);
    color: var(--bf-dusky-2);
  }

  .order-summary__row--savings.order-summary__row--secondary .order-summary__value {
    font-weight: var(--bf-fw-medium);
    color: var(--bf-dusky-2);
  }

  /* Foot position (DS 1.1.169): tight under the total, the total row's 16px moves below it. */
  .order-summary__totals-row:has(+ .order-summary__row--savings) {
    margin-bottom: 0;
  }

  .order-summary__foot > .order-summary__row--savings {
    padding: 4px 0 0;
    margin-bottom: 16px;
  }

  .order-summary__foot {
    position: relative;
    padding: 18px 22px;
    background: var(--bf-blue-light);
    border-top: 1px solid var(--bf-line);
  }

  .order-summary__totals-row {
    display: flex;
    align-items: baseline;
    justify-content: space-between;
    margin-bottom: 16px;
  }

  .order-summary__total-label {
    font-family: var(--bf-font-display);
    font-size: var(--bf-fs-sm);
    font-weight: var(--bf-fw-semibold);
    color: var(--bf-deep-blue);
  }

  .order-summary__total-value {
    font-family: var(--bf-font-display);
    font-size: 28px;
    font-weight: var(--bf-fw-bold);
    color: var(--bf-deep-blue);
    font-variant-numeric: tabular-nums;
    letter-spacing: var(--bf-tr-tight);
  }

  .order-summary__total-cur {
    font-size: var(--bf-fs-sm);
    font-weight: var(--bf-fw-semibold);
    color: var(--bf-dusky-2);
    letter-spacing: var(--bf-tr-wide);
  }

  .order-summary__security-note {
    display: flex;
    align-items: center;
    gap: 6px;
    margin-top: 12px;
    font-family: var(--bf-font-body);
    font-size: 13px;
    color: var(--bf-dusky-2);
  }

  .order-summary__security-note-icon {
    flex-shrink: 0;
    color: var(--bf-success);
  }

  @media (max-width: 980px) {
    .order-summary--sticky {
      position: static;
    }
  }
</style>
